import { AnchorButton } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { Text } from 'vet-bones/components';

import { Rule } from 'src/components/icons/Rule';
import { Link } from 'src/components/Link';
import {
  PROJECT_DIAGRAM_ENTITY_DRAWER_ATTRIBUTE_TYPE,
  TEST_IDS,
  getProjectDiagramEntityDrawerTableLinkTestId,
  getProjectDiagramEntityDrawerTableRowTestId,
} from 'src/constants/testIds';
import { AttributeTableItem } from 'src/containers/ProjectDiagram/components/attributesTable/types';
import {
  EditButton,
  UnlinkButton,
} from 'src/containers/ProjectDiagram/components/ProjectDiagramEntityDrawerButtons';
import { useOpenRulesForAttribute } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useOpenRulesForAttribute';
import * as copy from 'src/templates/copy';
import { colors } from 'src/templates/styles/colors';

type ProjectDiagramAttributesTableRowProps = {
  attribute: AttributeTableItem & { depth?: number };
  idx: number;
  isPreviousRowHighlighted?: boolean;
  onClickLink?: (item: AttributeTableItem) => void;
  onStartEdit?: () => void;
  onUnlink: (item: AttributeTableItem) => void;
};

export const ProjectDiagramAttributesTableRow: React.VFC<
  ProjectDiagramAttributesTableRowProps
> = ({
  attribute,
  idx,
  isPreviousRowHighlighted,
  onClickLink,
  onStartEdit,
  onUnlink,
}) => {
  const { openRules, openCreateRules } = useOpenRulesForAttribute(
    attribute.value
  );

  let ruleButtonComponent: React.ReactNode;
  if (openRules) {
    ruleButtonComponent = (
      <AnchorButton
        data-testid={TEST_IDS.projectDiagram.entityDrawer.ruleButton}
        icon={<Rule color={colors.mediumBlue} />}
        minimal
        onClick={openRules}
      />
    );
  } else if (openCreateRules) {
    ruleButtonComponent = (
      <Link onClick={openCreateRules}>
        <Text
          data-testid={TEST_IDS.projectDiagram.entityDrawer.createRuleButton}
        >
          {copy.components.projectDiagram.attributesTable.createRule}
        </Text>
      </Link>
    );
  }
  return (
    <tr
      className={classNames('sd-has-hidden-until-hover', {
        'sd-highlighted-table-row': attribute.highlighted,
        'sd-highlighted-table-row__no-top-border':
          attribute.highlighted && isPreviousRowHighlighted,
      })}
      data-testid={getProjectDiagramEntityDrawerTableRowTestId(
        attribute.value,
        PROJECT_DIAGRAM_ENTITY_DRAWER_ATTRIBUTE_TYPE,
        idx
      )}
    >
      <td>
        {onClickLink ? (
          <Link
            className="sd-drawer-project-diagram-entity-table-standin--input"
            onClick={() => onClickLink(attribute)}
            testId={getProjectDiagramEntityDrawerTableLinkTestId(
              attribute.value,
              PROJECT_DIAGRAM_ENTITY_DRAWER_ATTRIBUTE_TYPE,
              idx
            )}
          >
            <Text ellipsize>{attribute.text}</Text>
          </Link>
        ) : (
          <Text
            className="sd-drawer-project-diagram-entity-table-standin--input"
            ellipsize
          >
            {attribute.text}
          </Text>
        )}
      </td>
      <td>
        <Text className="sd-drawer-project-diagram-entity-table-standin--selector">
          {attribute.ranges.map((range) => range.text).join(', ')}
        </Text>
      </td>
      <td>{ruleButtonComponent}</td>
      <td>
        {onStartEdit ? (
          <EditButton
            onClick={() => {
              onStartEdit();
            }}
          />
        ) : null}
        <UnlinkButton
          disabled={!!attribute.depth && attribute.depth > 1}
          onClick={() => onUnlink(attribute)}
        />
      </td>
    </tr>
  );
};
